import classNames from "classnames";
import { memo, useCallback, useState } from "react";
import { CloseButton, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";

import { errorTextClass } from "@/components/ErrorSpan";
import { QuantityWithUOMAbbr } from "@/components/QuantityWithUOMAbbr";
import { maxPriorityOption, PriorityOption } from "@/features/purchasing-priority/purchasing-priority-utils";
import { BomComponent } from "../queries/tracking-data/bom-by-component";
import { AsciiTreeComponent, BomHierarchyGenerator, BomNode } from "../queries/tracking-data/bom-hierarchy-generator";
import { Item } from "../queries/tracking-data/items";
import { withJobTrackingData } from "./withJobTrackingData";
import { PurchasingPriorityBadge } from "@/features/purchasing-priority/PurchasingPriorityBadge";

export interface QuantityRequiredForJobCellProps {
  item: Item;
  priority?: PriorityOption;
}

/**
 * A component that renders the quantity of a BOM component required for the job. Clicking the rendered number will show
 * a popover with a calculation breakdown of how that quantity was calculated.
 */
export const QuantityRequiredForJobCell: React.FC<QuantityRequiredForJobCellProps> = memo(
  withJobTrackingData(function QuantityRequiredForJobCellInternal({
    salesOrderBomHierarchy,
    bomComponentsByItemId,
    item,
    priority,
  }) {
    const [showPopover, setShowPopover] = useState(false);
    const closePopover = useCallback(() => {
      setShowPopover(false);
    }, []);

    const bomComponent = bomComponentsByItemId.get(item.id);
    const quantityRequiredForJobAtPriority =
      bomComponent?.quantitiesByPriority.get(priority ?? maxPriorityOption)!.quantityRequiredForJob ?? 0;
    const filteredBomHierarchy = filterBomHierarchyForItem(salesOrderBomHierarchy, item.id);

    return (
      <OverlayTrigger
        trigger="click"
        show={showPopover}
        onToggle={setShowPopover}
        rootClose
        overlay={(props) => (
          <Popover
            {...props}
            className="d-flex flex-column"
            style={{ ...props.style, "--bs-popover-max-width": "500px", maxHeight: 300 }}
          >
            <Popover.Header as="h3">
              Total Quantity Breakdown For {item.name}
              <CloseButton className="float-end" onClick={closePopover} />
            </Popover.Header>
            <Popover.Body className="flex-fill overflow-y-auto">
              <TotalQuantityBreakdown
                filteredBomHierarchy={filteredBomHierarchy}
                item={item}
                bomComponent={bomComponent}
                priority={priority}
              />
            </Popover.Body>
          </Popover>
        )}
      >
        <button
          className={classNames("btn btn-link p-0", { [errorTextClass]: !bomComponent?.onSalesOrder })}
          title="Click to see breakdown"
        >
          {!bomComponent?.onSalesOrder && "⚠"}
          <QuantityWithUOMAbbr quantity={quantityRequiredForJobAtPriority} uom={item.baseItemUOM?.uom} />
        </button>
      </OverlayTrigger>
    );
  }),
);

const TotalQuantityBreakdown: React.FC<{
  filteredBomHierarchy: BomNode[];
  bomComponent: BomComponent | undefined;
  item: Item;
  priority: PriorityOption | undefined;
}> = ({ filteredBomHierarchy, bomComponent, item, priority }) => {
  // Return an error message if the item does not exist in the BOM hierarchy
  if (!filteredBomHierarchy.length) return <>Item {item.name} is not found anywhere in the BOM hierarchy.</>;

  // If the item exists in the hierarchy it should always have a corresponding BOM component
  if (!bomComponent) throw new Error(`No BOM component found for item ${item.name} that occurs in the BOM hierarchy.`);

  // Count the number of leaf nodes in the filtered BOM hierarchy (i.e. nodes where the item is found)
  const leafNodes = getLeafNodes(filteredBomHierarchy);

  const quantitiesForPriority = bomComponent.quantitiesByPriority.get(priority ?? maxPriorityOption)!;
  return (
    <>
      <p>
        The total quantity of <b>{item.name}</b> on the sales order BOM{" "}
        {priority && (
          <>
            for priority <PurchasingPriorityBadge priority={priority} />{" "}
          </>
        )}
        is{" "}
        <b>
          <QuantityWithUOMAbbr quantity={quantitiesForPriority.bomTotalQuantity} uom={item.baseItemUOM?.uom} />
        </b>
        .
      </p>
      {quantitiesForPriority.bomTotalQuantity !== quantitiesForPriority.quantityRequiredForJob && (
        <p>
          ⚠️ The quantity required for this order has{" "}
          {quantitiesForPriority.quantityRequiredForJob > quantitiesForPriority.bomTotalQuantity ?
            "increased"
          : "decreased"}{" "}
          to{" "}
          <b>
            <QuantityWithUOMAbbr quantity={quantitiesForPriority.quantityRequiredForJob} uom={item.baseItemUOM?.uom} />
          </b>{" "}
          due to movement of parent assemblies to/from stock or other jobs.
        </p>
      )}
      <p>
        Item <b>{item.name}</b> is found in <b>{leafNodes.length}</b> locations in the BOM hierarchy. Hover over any of
        the quantities below to see the breakdown of how that quantity is calculated.
      </p>
      <div className="lh-1">
        {filteredBomHierarchy.map((node) => (
          <BomNodeComponent key={node.path} parentTotalQuantity={null} node={node} priority={priority} />
        ))}
      </div>
    </>
  );
};

const BomNodeComponent: React.FC<{
  parentTotalQuantity: number | null;
  node: BomNode;
  priority: PriorityOption | undefined;
}> = ({ parentTotalQuantity, node, priority }) => {
  const isLeaf = node.children.length === 0;
  const prioritized = !priority || !isLeaf || node.purchasingPriority.priority <= priority;
  const nodeTotalQuantityForPriority = prioritized ? node.totalQuantity : 0;
  return (
    <>
      <pre className="m-0 overflow-y-hidden">
        {node.asciiTreePrefix}
        <span className={isLeaf ? "fw-bold text-primary" : ""}>
          {node.item.name}:{" "}
          <OverlayTrigger
            trigger={["hover", "focus"]}
            overlay={(props) => (
              <Tooltip {...props} style={{ ...props.style, "--bs-tooltip-max-width": "300px" }}>
                {!prioritized ?
                  <>
                    This component is filtered out because it has purchasing priority{" "}
                    <PurchasingPriorityBadge priority={node.purchasingPriority.priority} />
                  </>
                : parentTotalQuantity ?
                  <>
                    <QuantityWithUOMAbbr quantity={node.quantity} uom={node.item.baseItemUOM?.uom} /> per build x{" "}
                    {parentTotalQuantity} builds ={" "}
                    <QuantityWithUOMAbbr quantity={nodeTotalQuantityForPriority} uom={node.item.baseItemUOM?.uom} />
                  </>
                : <>{nodeTotalQuantityForPriority} on sales order</>}
              </Tooltip>
            )}
          >
            <span className="text-decoration-underline-dotted">
              <QuantityWithUOMAbbr quantity={nodeTotalQuantityForPriority} uom={node.item.baseItemUOM?.uom} />
            </span>
          </OverlayTrigger>
        </span>
      </pre>
      {node.children.map((child) => (
        <BomNodeComponent key={child.path} parentTotalQuantity={node.totalQuantity} node={child} priority={priority} />
      ))}
    </>
  );
};

function filterBomHierarchyForItem(bomHierarchy: BomNode[], itemId: number): BomNode[] {
  // Filter the BOM hierarchy to only include nodes that contain the specified item (or it's ancestors)
  const filteredBomHierarchy = bomHierarchy
    .map((node) => filterBomHierarchyForItemInternal(node, itemId))
    .filter((node) => node !== null);

  // Update each node in the tree to recalculate the ascii tree components
  return filteredBomHierarchy.map((node, index) =>
    updateFilteredBomHierarchy(node, null, index === filteredBomHierarchy.length - 1),
  );
}

function filterBomHierarchyForItemInternal(node: BomNode, itemId: number): BomNode | null {
  // If this item matches the item, return it with no children
  if (node.item.id === itemId) return { ...node, children: [] };

  // Recursively filter children for the item
  const children = node.children
    .map((child) => filterBomHierarchyForItemInternal(child, itemId))
    .filter((child) => child !== null);

  // Filter out this node if all its children have been filtered out
  if (children.length === 0) return null;

  // Otherwise, return this node with its filtered children
  return { ...node, children };
}

function updateFilteredBomHierarchy(
  node: BomNode,
  parentComponents: AsciiTreeComponent[] | null,
  isLastChild: boolean,
): BomNode {
  // Recalculate the ascii tree components for each node after filtering
  const asciiTreeComponents =
    parentComponents ? BomHierarchyGenerator.getAsciiTreeComponents(parentComponents, isLastChild) : [];

  return {
    ...node,
    asciiTreePrefix: asciiTreeComponents.join(""),
    children: node.children.map((child, index) =>
      updateFilteredBomHierarchy(child, asciiTreeComponents, index === node.children.length - 1),
    ),
  };
}

function getLeafNodes(nodes: BomNode[]): BomNode[] {
  return nodes.flatMap((node) => {
    if (node.children.length === 0) return [node];
    return getLeafNodes(node.children);
  });
}
